import { useCallback, useState } from 'react';
import { useMutation } from '../../gql/hooks';
import {
  PROBE_PROVIDER_MUTATION,
  type ProbeProviderMutationResult,
  type ProbeProviderMutationVariables,
  type ProviderProbeResult,
} from '../../gql/operations';

// Shared "test connection" controller (W3b). Wraps the probeProvider mutation so both the health
// board cards and the provider form modal expose the same loading / result / error surface. The
// last probe result is held locally so the caller can render the inline result chip.

export interface UseProbeProvider {
  /** Run a probe for the given provider id. */
  probe: (id: string) => Promise<void>;
  /** The most recent probe result, or null before the first probe. */
  result: ProviderProbeResult | null;
  loading: boolean;
  /** A user-facing error message when the probe request itself fails (not a probe failure status). */
  error: string | null;
  /** Clear the held result/error (e.g. when reopening a form). */
  reset: () => void;
}

export function useProbeProvider(): UseProbeProvider {
  const mutation = useMutation<
    ProbeProviderMutationResult,
    ProbeProviderMutationVariables
  >(PROBE_PROVIDER_MUTATION);
  const [result, setResult] = useState<ProviderProbeResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const probe = useCallback(
    async (id: string) => {
      setError(null);
      try {
        const data = await mutation.mutate({ id });
        setResult(data.probeProvider);
      } catch (cause: unknown) {
        const message =
          cause instanceof Error ? cause.message : 'The connection test could not be run.';
        setError(message);
      }
    },
    [mutation],
  );

  const reset = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  return { probe, result, loading: mutation.loading, error, reset };
}
